import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { PreboardingForm, PreboardingFormCompletionConfig } from '@hygge/shared';
import { useUpdatePreboardingForm } from '@/hooks/use-preboarding-forms';
import { CompletionPreview } from '../completion-preview';
import { RichVariableEditor } from '../rich-variable-editor';
import { SaveStatusIndicator } from '../save-status-indicator';
import { useDraftAutosave } from '../use-draft-autosave';
import type { PreboardingFormStepProps } from './setup-emails-step';

function toDraft(form: PreboardingForm): PreboardingFormCompletionConfig {
  const c = form.completionConfig;
  return {
    successTitle: c?.successTitle ?? 'Submission successful!',
    closingMessage: c?.closingMessage ?? '',
    feedbackLink: {
      enabled: c?.feedbackLink?.enabled ?? false,
      label: c?.feedbackLink?.label ?? '',
      url: c?.feedbackLink?.url ?? '',
    },
  };
}

// Step 4 — Completion Screen (HW-559). Configures the success page shown after
// the candidate submits: title, closing message, and an optional feedback link.
// Autosaves the draft; the wizard's "Publish form" button is the Finish action.
export function CompletionStep({ form }: PreboardingFormStepProps) {
  const updateMutation = useUpdatePreboardingForm({ suppressGlobalToast: true });

  const { draft, applyChange, status, lastSavedAt, saveNow } =
    useDraftAutosave<PreboardingFormCompletionConfig>({
      initial: toDraft(form),
      onSave: async (next) => {
        await updateMutation.mutateAsync({ id: form.id, input: { completionConfig: next } });
      },
    });

  const link = draft.feedbackLink;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_minmax(320px,420px)]">
      {/* Configuration */}
      <div className="space-y-6">
        <section className="space-y-4 rounded-xl border border-border bg-card p-5">
          <div className="space-y-1.5">
            <Label htmlFor="success-title">Success title</Label>
            <Input
              id="success-title"
              value={draft.successTitle}
              placeholder="Submission successful!"
              onChange={(e) => applyChange({ ...draft, successTitle: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Closing message</Label>
            <RichVariableEditor
              content={draft.closingMessage}
              onChange={(html) => applyChange({ ...draft, closingMessage: html })}
              placeholder="Your documents have been received. Our HR team will reach out with the next steps, {{employeeFirstName}}…"
            />
          </div>
        </section>

        <section className="space-y-4 rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="feedback-enabled" className="text-sm">External feedback link</Label>
              <p className="text-[11px] text-muted-foreground">
                Show a button below the message — e.g. a feedback survey or company handbook.
              </p>
            </div>
            <Switch
              id="feedback-enabled"
              checked={link.enabled}
              onCheckedChange={(checked) =>
                applyChange({ ...draft, feedbackLink: { ...link, enabled: checked } })
              }
            />
          </div>

          {link.enabled && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="feedback-label">Button label</Label>
                <Input
                  id="feedback-label"
                  value={link.label}
                  placeholder="Rate your experience"
                  onChange={(e) =>
                    applyChange({ ...draft, feedbackLink: { ...link, label: e.target.value } })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="feedback-url">Target URL</Label>
                <Input
                  id="feedback-url"
                  type="url"
                  value={link.url}
                  placeholder="https://forms.gle/…"
                  onChange={(e) =>
                    applyChange({ ...draft, feedbackLink: { ...link, url: e.target.value } })
                  }
                />
              </div>
            </div>
          )}
        </section>

        <div className="flex justify-end">
          <SaveStatusIndicator status={status} lastSavedAt={lastSavedAt} onRetry={saveNow} />
        </div>
      </div>

      {/* Live preview */}
      <div className="lg:sticky lg:top-4 lg:self-start">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Preview
        </p>
        <CompletionPreview
          successTitle={draft.successTitle}
          closingMessage={draft.closingMessage}
          feedbackLink={link}
        />
      </div>
    </div>
  );
}
